// Pointer, touch and keyboard input for the viewer, independent of the camera mode.
//
// Desktop: one click starts free look: the cursor disappears (Pointer Lock) and moving the mouse
// turns the view, no button held. The next click (or Esc) ends it and the cursor is back. Clicking
// never walks anywhere; walking is WASD/arrows. Where Pointer Lock is unavailable, the same toggle
// works with a hidden cursor inside the canvas.
// Touch: two joysticks, the left one walks, the right one turns the view (each starts where the thumb lands in its
// lower corner; resting rings show where they are). Elsewhere drag the scene to look, tap to walk there, pinch to
// zoom. A quick tap inside a joystick corner still walks there.
// Motion look (phones, tablets): turning the device turns the view; touch and joysticks add to it.

const TAP_MAX_MOVE = 8;     // px
const TAP_MAX_TIME = 350;   // ms
const STICK_MAX = 50;       // px of knob travel for full deflection
const DEG = Math.PI / 180;
const wrap = d => ((d % 360) + 540) % 360 - 180;

export class Input {
    constructor(el) {
        this.el = el;
        this.look = [0, 0];          // touch drag in px since last read
        this.mouse = [0, 0];         // free-look mouse movement in px since last read
        this.zoom = 0;               // accumulated wheel / pinch
        this.keys = new Set();
        this.joy = null;             // walk stick {id, x0, y0, t, moved}
        this.joyVec = [0, 0];        // -1..1, y up = forward
        this.lookJoy = null;         // look stick, same shape
        this.lookVec = [0, 0];       // -1..1, x right, y up
        this.sticksEnabled = true;   // main switches them off in the overview
        this.stickEls = {};          // {move, look} joystick elements
        this.gyro = null;            // {ref: {heading, pitch}} while motion look is on
        this.tilt = [0, 0];          // yaw, pitch in degrees from turning the device since last read
        this.taps = [];              // touch taps [{x, y}] (client coords relative to the canvas)
        this.pointers = new Map();
        this.mouseLook = false;      // free look active
        this.lockMode = null;        // 'pointerlock' | 'fallback'
        this.lookEnabled = true;     // main switches it off during the drone tour
        this.clicks = 0;             // mouse presses, so the tour can be interrupted by a click
        this.onUserInput = null;     // "user did something" (cancels auto-walk)
        this.onLookChange = null;    // (active) => void

        el.style.touchAction = 'none';
        el.addEventListener('pointerdown', e => this._down(e));
        el.addEventListener('pointermove', e => this._move(e));
        el.addEventListener('pointerup', e => this._up(e));
        el.addEventListener('pointercancel', e => this._up(e, true));
        el.addEventListener('wheel', e => { e.preventDefault(); this.zoom += e.deltaY; }, { passive: false });
        el.addEventListener('contextmenu', e => e.preventDefault());
        document.addEventListener('pointerlockchange', () => {
            const locked = document.pointerLockElement === el;
            if (!locked && this.lockMode === 'pointerlock') this._setLook(false);
        });
        document.addEventListener('pointerlockerror', () => {
            // Denied (embedding, browser policy): same behaviour with a hidden cursor
            if (this.lockMode === 'pointerlock') { this.lockMode = 'fallback'; this._setLook(true); }
        });
        window.addEventListener('keydown', e => {
            if (e.target instanceof HTMLInputElement) return;
            const k = e.key.toLowerCase();
            if (k === 'escape' && this.mouseLook) this.stopLook();
            if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'shift', 'q', 'e'].includes(k)) {
                this.keys.add(k);
                this.onUserInput?.();
                if (k.startsWith('arrow')) e.preventDefault();
            }
        });
        window.addEventListener('keyup', e => this.keys.delete(e.key.toLowerCase()));
        window.addEventListener('blur', () => { this.keys.clear(); this.stopLook(); });
        // Back from another app: the device may have turned meanwhile, start from where it points now
        document.addEventListener('visibilitychange', () => { if (this.gyro) this.gyro.ref = null; });
    }

    _down(e) {
        if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
            if (e.button > 0) return;
            this.clicks++;
            if (!this.lookEnabled) return;
            // Press toggles free look (a pointerdown is a user gesture, as Pointer Lock requires)
            if (this.mouseLook) this.stopLook();
            else this.startLook();
            return;
        }
        this.el.setPointerCapture?.(e.pointerId);
        const rect = this.el.getBoundingClientRect();
        const lx = e.clientX - rect.left, ly = e.clientY - rect.top;
        // Touch in a lower corner starts that corner's joystick where the thumb landed
        const stick = this._stickZone(lx, ly, rect);
        if (stick) {
            const j = { id: e.pointerId, x0: e.clientX, y0: e.clientY, t: performance.now(), moved: 0 };
            if (stick === 'move') this.joy = j; else this.lookJoy = j;
            this._showStick(stick, lx, ly);
            this.onUserInput?.();
            return;
        }
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: performance.now(), moved: 0 });
    }

    _move(e) {
        if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
            if (this.mouseLook) {
                this.mouse[0] += e.movementX || 0;
                this.mouse[1] += e.movementY || 0;
                if (e.movementX || e.movementY) this.onUserInput?.();
            }
            return;
        }
        const stick = this._stickOf(e.pointerId);
        if (stick) {
            const j = stick === 'move' ? this.joy : this.lookJoy;
            let dx = e.clientX - j.x0, dy = e.clientY - j.y0;
            const len = Math.hypot(dx, dy);
            j.moved = Math.max(j.moved, len);
            if (len > STICK_MAX) { dx *= STICK_MAX / len; dy *= STICK_MAX / len; }
            const v = [dx / STICK_MAX, -dy / STICK_MAX];
            if (stick === 'move') this.joyVec = v; else this.lookVec = v;
            this._moveKnob(stick, dx, dy);
            return;
        }
        const p = this.pointers.get(e.pointerId);
        if (!p) return;
        const dx = e.clientX - p.x, dy = e.clientY - p.y;
        p.x = e.clientX; p.y = e.clientY;
        p.moved = Math.max(p.moved, Math.hypot(e.clientX - p.sx, e.clientY - p.sy));
        if (this.pointers.size === 2) {
            // Pinch: change of finger distance -> zoom
            const [a, b] = [...this.pointers.values()];
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            if (this._pinch) this.zoom += (this._pinch - d) * 2;
            this._pinch = d;
            return;
        }
        if (p.moved > TAP_MAX_MOVE) {
            this.look[0] += dx;
            this.look[1] += dy;
            this.onUserInput?.();
        }
    }

    _up(e, cancel = false) {
        if (e.pointerType === 'mouse' || e.pointerType === 'pen') return;
        const stick = this._stickOf(e.pointerId);
        if (stick) {
            const j = stick === 'move' ? this.joy : this.lookJoy;
            if (stick === 'move') { this.joy = null; this.joyVec = [0, 0]; } else { this.lookJoy = null; this.lookVec = [0, 0]; }
            this._restStick(stick);
            if (!cancel && j.moved <= TAP_MAX_MOVE && performance.now() - j.t < TAP_MAX_TIME) {
                const rect = this.el.getBoundingClientRect();
                this.taps.push({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
            return;
        }
        const p = this.pointers.get(e.pointerId);
        this.pointers.delete(e.pointerId);
        if (this.pointers.size < 2) this._pinch = null;
        if (!p || cancel) return;
        if (p.moved <= TAP_MAX_MOVE && performance.now() - p.t < TAP_MAX_TIME) {
            const rect = this.el.getBoundingClientRect();
            this.taps.push({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
    }

    startLook() {
        if (this.mouseLook) return;
        if (this.el.requestPointerLock && this.lockMode !== 'fallback') {
            this.lockMode = 'pointerlock';
            try {
                const r = this.el.requestPointerLock({ unadjustedMovement: true });
                // Some browsers reject unadjustedMovement; retry plain, then fall back
                r?.catch?.(() => this.el.requestPointerLock()?.catch?.(() => { this.lockMode = 'fallback'; this._setLook(true); }));
            } catch {
                this.lockMode = 'fallback';
            }
        } else {
            this.lockMode = 'fallback';
        }
        this._setLook(true);
    }

    stopLook() {
        if (!this.mouseLook) return;
        if (document.pointerLockElement === this.el) document.exitPointerLock();
        this._setLook(false);
    }

    _setLook(active) {
        if (this.mouseLook === active) return;
        this.mouseLook = active;
        this.mouse = [0, 0];
        this.onLookChange?.(active);
    }

    /** 'move' (lower left) or 'look' (lower right) if a touch at canvas point (x, y) starts a joystick. */
    _stickZone(x, y, rect) {
        if (!this.sticksEnabled || y < rect.height * 0.55) return null;
        if (!this.joy && x < rect.width * 0.4) return 'move';
        if (!this.lookJoy && x > rect.width * 0.6) return 'look';
        return null;
    }

    _stickOf(id) {
        return this.joy?.id === id ? 'move' : this.lookJoy?.id === id ? 'look' : null;
    }

    _stickEl(which) {
        let el = this.stickEls[which];
        if (!el) {
            el = this.stickEls[which] = document.createElement('div');
            el.className = `stick stick-${which}`;
            el.innerHTML = '<div class="stick-knob"></div>';
            this.el.parentElement.appendChild(el);
        }
        return el;
    }

    /** Resting rings in the two lower corners, so a touch screen shows both joysticks before the first touch. */
    showSticks() {
        this._stickEl('move');
        this._stickEl('look');
    }

    _showStick(which, x, y) {
        const el = this._stickEl(which);
        el.classList.add('on');
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        this._moveKnob(which, 0, 0);
    }

    _moveKnob(which, dx, dy) {
        this.stickEls[which].firstChild.style.transform = `translate(${dx}px, ${dy}px)`;
    }

    _restStick(which) {
        const el = this.stickEls[which];
        el.classList.remove('on');
        el.style.left = el.style.top = '';
        this._moveKnob(which, 0, 0);
    }

    /**
     * Motion look: turning the device turns the view. Asks for the sensor where the browser requires it (iOS, from
     * a tap). Resolves false without a sensor reading within a second or without permission.
     */
    async startGyro() {
        const DOE = window.DeviceOrientationEvent;
        if (!DOE) return false;
        if (typeof DOE.requestPermission === 'function') {
            try { if (await DOE.requestPermission() !== 'granted') return false; } catch { return false; }
        }
        this.gyro = { ref: null };
        this.tilt = [0, 0];
        this._onOrient ??= (e) => this._orient(e);
        window.addEventListener('deviceorientation', this._onOrient);
        const ok = await new Promise((resolve) => {
            this.gyro.seen = resolve;
            setTimeout(() => resolve(false), 1000);
        });
        if (!ok) this.stopGyro();
        return ok;
    }

    stopGyro() {
        if (this._onOrient) window.removeEventListener('deviceorientation', this._onOrient);
        this.gyro = null;
        this.tilt = [0, 0];
    }

    get gyroOn() {
        return !!this.gyro;
    }

    _orient(e) {
        const g = this.gyro;
        if (!g || e.alpha === null || e.beta === null || e.gamma === null) return;
        g.seen?.(true);
        g.seen = null;
        // Where the back of the device points (its camera's view) in the sensor's frame (x east, y north, z up);
        // independent of how the screen is turned
        const a = e.alpha * DEG, b = e.beta * DEG, c = e.gamma * DEG;
        const x = -Math.sin(c) * Math.cos(a) - Math.cos(c) * Math.sin(b) * Math.sin(a);
        const y = -Math.sin(c) * Math.sin(a) + Math.cos(c) * Math.sin(b) * Math.cos(a);
        const pitch = Math.asin(Math.max(-1, Math.min(1, -Math.cos(b) * Math.cos(c)))) / DEG;
        const heading = Math.atan2(y, x) / DEG;   // counter-clockwise seen from above, like the camera's yaw
        if (!g.ref) { g.ref = { heading, pitch }; return; }
        // Pointing nearly straight up or down the heading is undefined: follow the tilt only
        const steep = Math.abs(pitch) > 75;
        if (steep) g.ref.heading = heading;
        const dh = steep ? 0 : wrap(heading - g.ref.heading), dp = pitch - g.ref.pitch;
        // Sensor jitter of a device at rest would keep the picture redrawing: changes this small wait until they add up
        if (Math.abs(dh) < 0.04 && Math.abs(dp) < 0.04) return;
        this.tilt[0] += dh;
        this.tilt[1] += dp;
        g.ref = { heading, pitch };
    }

    /** True while the visitor is actively doing something (used to interrupt the drone tour). */
    get busy() {
        return !!(this.pointers.size || this.joy || this.lookJoy || this.moveIntent().active);
    }

    /** Movement intent from keys and joystick: [right, forward] in -1..1, plus run flag. */
    moveIntent() {
        const k = this.keys;
        let x = (k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0);
        let y = (k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0);
        x += this.joyVec[0];
        y += this.joyVec[1];
        const len = Math.hypot(x, y);
        if (len > 1) { x /= len; y /= len; }
        const run = k.has('shift') || Math.hypot(...this.joyVec) > 0.95;
        return { x, y, run, active: len > 0.05 };
    }

    /** Touch drag since the last call (scene follows the finger). */
    takeLook() {
        const l = this.look;
        this.look = [0, 0];
        return l;
    }

    /** Look stick deflection: [right, up] in -1..1 (the view keeps turning while it is held). */
    lookStick() {
        return this.lookVec;
    }

    /** Device turn since the last call: [yaw, pitch] in degrees (motion look). */
    takeGyro() {
        const t = this.tilt;
        this.tilt = [0, 0];
        return t;
    }

    /** Free-look mouse movement since the last call (view follows the mouse, like a game). */
    takeMouse() {
        const m = this.mouse;
        this.mouse = [0, 0];
        return m;
    }

    takeZoom() {
        const z = this.zoom;
        this.zoom = 0;
        return z;
    }

    takeTaps() {
        const t = this.taps;
        this.taps = [];
        return t;
    }
}
